/** Support issue categories for Help & Support → Contact (single source for in-app copy). */

export const SUPPORT_MESSAGE_MIN = 20;
export const SUPPORT_MESSAGE_MAX = 2000;

/** @typedef {'general' | 'urgent'} SupportRoute */

/**
 * @typedef {Object} SupportSubcategory
 * @property {string} id
 * @property {string} title
 * @property {string} subtitle
 * @property {SupportRoute} route
 */

/**
 * @typedef {Object} SupportGroup
 * @property {string} id
 * @property {string} title
 * @property {SupportSubcategory[]} [items]
 * @property {boolean} [directForm]
 * @property {SupportRoute} [route]
 * @property {string} [formHint]
 */

export const SUPPORT_GROUPS = [
  {
    id: 'getting_started',
    title: 'Getting started',
    items: [
      {
        id: 'account_access',
        title: 'Account & Whop',
        subtitle: 'Login, disabled account, or Whop access issues',
        route: 'general',
      },
      {
        id: 'billing_whop',
        title: 'Billing & Whop membership',
        subtitle: 'Payment, renewal, or product access on Whop',
        route: 'general',
      },
      {
        id: 'documents_consent',
        title: 'Documents & consent',
        subtitle: 'Disclosures, SignWell signing, or 90-day reconfirm',
        route: 'general',
      },
    ],
  },
  {
    id: 'setup',
    title: 'Setup',
    items: [
      {
        id: 'channel_setup',
        title: 'Channel configuration',
        subtitle: 'Mappings, validation errors, or missing channels',
        route: 'general',
      },
      {
        id: 'tradier_credentials',
        title: 'Tradier / brokerage setup',
        subtitle: 'API token, Account ID, or connection errors',
        route: 'general',
      },
    ],
  },
  {
    id: 'discord',
    title: 'Discord',
    items: [
      {
        id: 'discord_setup',
        title: 'Discord & trade DMs',
        subtitle: 'User ID, DMs blocked, or approve/reject buttons',
        route: 'general',
      },
      {
        id: 'trading_fully_blocked',
        title: 'Trading completely stopped',
        subtitle: 'No DMs and no trades — nothing works across my setup',
        route: 'urgent',
      },
    ],
  },
  {
    id: 'trading',
    title: 'Trading',
    items: [
      {
        id: 'trade_failed',
        title: 'A trade didn’t work',
        subtitle: 'One signal failed, limits hit, or error DM for a trade',
        route: 'general',
      },
      {
        id: 'trade_history',
        title: 'Trade history & records',
        subtitle: 'Missing or incorrect trades in Trade History',
        route: 'general',
      },
      {
        id: 'auto_approve',
        title: 'Auto-approve & reconfirmation',
        subtitle: 'Enable, reconfirm, or auto-approve turned off',
        route: 'general',
      },
      {
        id: 'cannot_close_position',
        title: 'Can’t close an open position',
        subtitle: 'STC keeps failing or I can’t exit a position I hold',
        route: 'urgent',
      },
    ],
  },
  {
    id: 'activity',
    title: 'Activity',
    items: [
      {
        id: 'notifications',
        title: 'Notifications & alerts',
        subtitle: 'Email, SMS, or failure alerts not arriving',
        route: 'general',
      },
      {
        id: 'settings_profile',
        title: 'Settings & password',
        subtitle: 'Password, phone number, or profile preferences',
        route: 'general',
      },
    ],
  },
  {
    id: 'account',
    title: 'Account',
    items: [
      {
        id: 'security_incident',
        title: 'Security — compromised or suspicious',
        subtitle: 'Leaked API token or unauthorized activity',
        route: 'urgent',
      },
    ],
  },
  {
    id: 'other',
    title: 'Other',
    directForm: true,
    route: 'general',
    formHint: 'Describe your issue and we’ll email you a ticket confirmation.',
    items: [],
  },
];

export const DEFAULT_SUPPORT_GROUP_ID = SUPPORT_GROUPS[0]?.id ?? null;

const URGENT_CATEGORY_IDS = new Set(
  SUPPORT_GROUPS.flatMap((group) =>
    (group.items ?? []).filter((item) => item.route === 'urgent').map((item) => item.id)
  )
);

/** @returns {SupportGroup | undefined} */
export function getSupportGroupById(groupId) {
  return SUPPORT_GROUPS.find((group) => group.id === groupId);
}

/** @returns {SupportSubcategory | undefined} */
export function getSupportSubcategoryById(categoryId) {
  for (const group of SUPPORT_GROUPS) {
    const match = (group.items ?? []).find((item) => item.id === categoryId);
    if (match) {
      return match;
    }
  }
  if (categoryId === 'other') {
    const otherGroup = getSupportGroupById('other');
    if (otherGroup) {
      return {
        id: 'other',
        title: otherGroup.title,
        subtitle: otherGroup.formHint ?? '',
        route: 'general',
      };
    }
  }
  return undefined;
}

/** @returns {SupportRoute | null} */
export function getSupportRouteForCategory(categoryId) {
  if (categoryId === 'other') {
    return 'general';
  }
  const item = getSupportSubcategoryById(categoryId);
  return item?.route ?? null;
}

export function isUrgentSupportCategory(categoryId) {
  return URGENT_CATEGORY_IDS.has(categoryId);
}

export function isDirectFormSupportGroup(group) {
  return Boolean(group?.directForm);
}

/**
 * @param {string} message
 * @returns {string | null} Error message when invalid; null when OK.
 */
export function validateSupportMessage(message) {
  const trimmed = typeof message === 'string' ? message.trim() : '';
  if (!trimmed) {
    return 'Please describe your issue.';
  }
  if (trimmed.length < SUPPORT_MESSAGE_MIN) {
    return `Please enter at least ${SUPPORT_MESSAGE_MIN} characters.`;
  }
  if (trimmed.length > SUPPORT_MESSAGE_MAX) {
    return `Please keep your message under ${SUPPORT_MESSAGE_MAX} characters.`;
  }
  return null;
}
